import { z } from "zod";

export interface SearchConfig {
    searchOn: string[];
    label: string;
    placeholder: string;
    minMatchCharLength: number;
}

export interface FundExplorerProps {
    config: FundExplorerConfig;
}

export type FundExplorerConfigPartial = Partial<FundExplorerConfig>;
export interface FundExplorerConfig {
    groupOptions: GroupOption[];
    downloadFields: Array<string[]>;
    downloadFileName: string;
    filters: Filters;
    primaryFilters: string[];
    secondaryFilters: string[];
    fundColumns: Array<string[] | string>;
    tabs: Tab[];
    tabDates: TabDate[];
    searchSettings: SearchSettings;
    cardSettings: CardSettings;
    searchHashLabel: string;
    groupHashLabel: string;
    localStorageKey: string;
    searchPlaceholder: string;
    searchHelpText: boolean;
    field_configuration: number;
    shareclassNameColumnCodeName: ShareclassNameColumnCodeName;
    fundOrderingColumn: string;
    featuredFundsLabel: string;
    allFundsLabel: string;
    groupLabelSuffix: string;
    groupLabelOverrides: GroupLabelOverrides;
    noResultsSettings: NoResultsSettings;
    greyFilterTooltip: string;
    greyFilterTooltipDelay: number;
    moreFiltersShow: string;
    moreFiltersHide: string;
    columnCodeNames: ColumnCodeNames;
    filteredFundsLabel: string;
    groupAlwaysOpen: string[];
    strings: Strings;
    alwaysDisplaySecondaryFilters: boolean;
    disclaimerSets: DisclaimerSets;
    columnSets: ColumnSet[];
    headerTooltips: HeaderTooltips;
    id: number;
    url: string;
    audience_id: number;
    locale: string;
    language_id: number;
    search: SearchConfig;
}

export interface CardSettings {
    textFields: string[];
    documentFields: string[];
    assetField: string;
    usePrimaryShareclass: boolean;
    hashLabel: string;
    linkField: string;
}

export interface ColumnCodeNames {
    shareclassName: ShareclassNameColumnCodeName;
    fundName: string;
    fundType: string;
    shareclassCode: string;
}

export enum ShareclassNameColumnCodeName {
    ShareclassPageURL = "shareclassPageURL",
    ShortName = "shortName",
}

export interface ColumnSet {
    value: string[];
    columns: Array<ShareclassNameColumnCodeName[] | string>;
    disclaimers: Disclaimer[];
    fundDependencies?: FundDependencies;
}

export enum Disclaimer {
    GlobalIBORReformsDisclaimer = "globalIBORReformsDisclaimer",
    NewUnlaunchedFundsDisclaimer = "newUnlaunchedFundsDisclaimer",
    UkPropertyDisclaimer = "ukPropertyDisclaimer",
    WorldwideTrustMessage = "worldwideTrustMessage",
    WorldwideTrustMessagePrivate = "worldwideTrustMessagePrivate",
}

export interface FundDependencies {
    bidPrice: EuRdifferenceNav;
    offerPrice: EuRdifferenceNav;
    bidPrice__date: EuRdifferenceNav;
    lvNAV: EuRdifferenceNav;
    EURlvNAV: EuRdifferenceNav;
    mtmNAV: EuRdifferenceNav;
    EURmtmNAV: EuRdifferenceNav;
    differenceNAV: EuRdifferenceNav;
    EURdifferenceNAV: EuRdifferenceNav;
    lvNAV__date: EuRdifferenceNav;
    accNAV: EuRdifferenceNav;
    midPrice: MidPrice;
    midPrice__date: MidPrice;
    valuationTime: MidPrice;
}

export interface EuRdifferenceNav {
    priceType: PriceType[];
}

export enum PriceType {
    BidOffer = "Bid/Offer",
    Eulvnav = "EULVNAV",
    Lvnav = "LVNAV",
}

export interface MidPrice {
    priceType: Array<null | string>;
}

export interface DisclaimerSets {
    globalIBORReformsDisclaimer: GlobalIborReformsDisclaimer;
    ukPropertyDisclaimer: GlobalIborReformsDisclaimer;
    newUnlaunchedFundsDisclaimer: GlobalIborReformsDisclaimer;
    worldwideTrustMessage: GlobalIborReformsDisclaimer;
    worldwideTrustMessagePrivate: GlobalIborReformsDisclaimer;
}

export interface GlobalIborReformsDisclaimer {
    codeName: string;
    icon: null;
    level: string;
    documents: any[];
    aboveTable: boolean;
}

export interface Filters {
    domicile: AccInc;
    baseCurrency: AccInc;
    accInc: AccInc;
    assetClass: AssetClass;
    style: AssetClass;
    productType: AssetClass;
    sfdrClassification: AssetClass;
    sector: AccInc;
}

export interface AccInc {
    multi: boolean;
    required: boolean;
    options: Option[];
    level: string;
    label: boolean;
    innerLabel: string;
    type: string;
    hashLabel: string;
    filterChoices?: boolean;
}

export interface Option {
    value: string;
    label: string;
}

export interface AssetClass {
    level: string;
    options: Option[];
    multi: boolean;
    required: boolean;
    label: string;
    type: string;
    hashLabel: string;
    filterChoices: boolean;
    tooltip?: string;
}

export interface GroupLabelOverrides {
    "Unit Trust": string;
}

export interface GroupOption {
    label: string;
    hashLabel: string;
    value: string;
    ordering: string[];
}

export interface HeaderTooltips {
    midPrice: string;
    bidPrice: string;
    offerPrice: string;
    lvNAV: string;
    EURlvNAV: string;
    mtmNAV: string;
    EURmtmNAV: string;
    accNAV: string;
    ocf: string;
    mex: string;
    performanceBasis: string;
}

export interface NoResultsSettings {
    showMessage: boolean;
    heading: string;
    text: string;
    clearFilters: string;
}

export interface SearchSettings {
    threshold: number;
    searchFundColumns: SearchColumn[];
    searchShareClassColumns: SearchColumn[];
}

export interface SearchColumn {
    name: string;
    maxScore: number;
    fuzziness: number;
    type: string;
    minLength?: number;
}

export interface Strings {
    mobileFiltersButtonText: string;
}

export interface TabDate {
    value: string[];
    dateType: string;
    format: string;
}

export interface Tab {
    name: string;
    subtabs?: Subtab[];
}

export interface Subtab {
    name: string;
}

export type SimplePartProps = {
    partId: number;
    audienceId: number;
    version?: string;
    languageId?: number;
    fundId?: number;
};

export const SimplePartSchema = z.object({
    title: z.string().optional(),
    text: z.string().optional(),
    image: z.string().optional(),
    routes: z
        .object({
            url: z.string(),
            link_text: z.string(),
            new_window: z.boolean().default(false),
            rank: z.number(),
        })
        .array()
        .optional(),
});
